import type { Order } from '../api/orders'
import QRCode from 'react-qr-code'
import { getSafePaymentActionUrl } from '../utils/paymentUrls'
import styles from './PaymentInstructions.module.css'

type InstructionRecord = Record<string, unknown>

interface PaymentInstructionsProps {
  order: Order
}

export function PaymentInstructions({ order }: PaymentInstructionsProps) {
  const instructions = asRecord(order.paymentInstructions)
  const provider = order.paymentProvider ?? text(instructions, 'provider')
  const channel = order.paymentChannel ?? text(instructions, 'channel') ?? text(instructions, 'channelCode')
  const qrUrl = text(instructions, 'qrUrl')
  const qrString = text(instructions, 'qrString')
  const virtualAccountNumber = text(instructions, 'virtualAccountNumber')
  const bank = text(instructions, 'bank')
  const billKey = text(instructions, 'billKey')
  const billerCode = text(instructions, 'billerCode')
  const paymentCode = text(instructions, 'paymentCode')
  const actionUrl = getSafePaymentActionUrl(
    text(instructions, 'redirectUrl') ?? text(instructions, 'deeplinkUrl') ?? order.paymentUrl
  )
  const expiresAt = order.paymentExpiresAt ?? text(instructions, 'expiresAt')

  if (!instructions && !actionUrl) {
    return null
  }

  return (
    <section className={styles.instructions} aria-label="Payment instructions">
      <div className={styles.header}>
        <div>
          <span className={styles.eyebrow}>PAYMENT INSTRUCTIONS</span>
          <h2 className={styles.title}>
            {[provider, channel].filter(Boolean).join(' / ') || 'DIRECT PAYMENT'}
          </h2>
        </div>
        {expiresAt && (
          <span className={styles.expiry}>EXPIRES {formatDateTime(expiresAt)}</span>
        )}
      </div>

      {qrUrl && (
        <div className={styles.qrBlock}>
          <img src={qrUrl} alt="Payment QR code" className={styles.qrImage} />
        </div>
      )}
      {!qrUrl && qrString && (
        <div className={styles.qrBlock}>
          <QRCode
            value={qrString}
            title="Payment QR code"
            size={256}
            viewBox="0 0 256 256"
            className={styles.qrImage}
          />
        </div>
      )}

      <div className={styles.grid}>
        {virtualAccountNumber && (
          <InstructionValue
            label={bank ? `${bank} VA` : 'Virtual Account'}
            value={virtualAccountNumber}
          />
        )}
        {billKey && <InstructionValue label="Bill Key" value={billKey} />}
        {billerCode && <InstructionValue label="Biller Code" value={billerCode} />}
        {paymentCode && <InstructionValue label="Payment Code" value={paymentCode} />}
        {qrString && <InstructionValue label="QR String" value={qrString} multiline />}
      </div>

      {actionUrl && (
        <a href={actionUrl} target="_blank" rel="noopener noreferrer" className={styles.action}>
          OPEN PAYMENT ACTION
        </a>
      )}
    </section>
  )
}

function InstructionValue({
  label,
  value,
  multiline,
}: {
  label: string
  value: string
  multiline?: boolean
}) {
  return (
    <div className={styles.valueBlock} data-multiline={multiline ? 'true' : undefined}>
      <span className={styles.valueLabel}>{label}</span>
      <span className={styles.valueText}>{value}</span>
    </div>
  )
}

function asRecord(value: unknown): InstructionRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  return value as InstructionRecord
}

function text(record: InstructionRecord | null, key: string): string | null {
  const value = record?.[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}
